import { Box, Stack, Button, Typography, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Fade, TablePagination, Autocomplete, TextField, Tooltip } from '@mui/material'
import React, { useEffect, useState, useRef } from 'react'
import { Colors } from '../../../Styles/Colors'
import RestAPIService from '../../../Utility/Services/RestAPIService'
import ToastMsg from '../../../Components/ToastMsg/ToastMsg'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { localGetItem } from '../../../Utility/Services/CacheProviderService';
import { keyframes } from "@emotion/react";
import Loader from '../../../Components/Loader';
import { FileDownloadOutlined } from '@mui/icons-material'

const glowAnim = keyframes`
  0% { box-shadow: 0 0 10px rgba(0, 255, 128, 0.4); }
  50% { box-shadow: 0 0 25px rgba(0, 255, 128, 0.8); }
  100% { box-shadow: 0 0 10px rgba(0, 255, 128, 0.4); }
`;

const migrateType = ["Drug Only", "Drug & Stock"]

export default function PatientMigration({ moduleName, api }) {
    const pollingRef = useRef(null);
    const [migrationId, setMigrationId] = useState(null);
    const [summary, setSummary] = useState([]);
    const [progressPercent, setProgressPercent] = useState(0);
    const [errorMsg, setErrorMsg] = useState(false);
    const [errorMsgText, setErrorMsgText] = useState('');
    const [successMsg, setSuccessMsg] = useState(false);
    const [successMsgText, setSuccessMsgText] = useState('');
    const [isLoader, setIsLoader] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [progressBar, setProgressBar] = useState(false);
    const [validating, setValidating] = useState(false);
    const [selectedmigrate, setselectedmigrate] = useState(migrateType[0]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const msgClose = () => {
        setErrorMsg(false);
        setErrorMsgText('');
        setSuccessMsg(false);
        setSuccessMsgText('');
    };

    const errorMessage = (msg) => {
        setErrorMsgText(msg);
        setErrorMsg(true);
    };

    const successMessage = (msg) => {
        setSuccessMsgText(msg);
        setSuccessMsg(true);
    };

    useEffect(() => {
        let clinicId = localGetItem('clinicId');

        const fetchCurrentStatus = async () => {
            try {
                setIsLoader(true);
                const response = await RestAPIService.getAll(`${api.progress}?clinic_id=${clinicId}&migration_type=${moduleName}`);

                if (response.data?.results?.status === 'success' || response.data?.status === 'success') {
                    const data = response.data?.results?.data?.[0] || response.data?.data?.[0];
                    setIsLoader(false);

                    if (!data) return;

                    if (data.status === "Validating") {
                        setValidating(true)
                    }

                    setMigrationId((data?.status === 'initiated' || data?.status === 'Validated' || (moduleName === "Pharmacy" && data?.status === 'Failed')) ? data.upload_id : null);
                    setSummary(response.data?.results?.data || response.data?.data || []);
                    setselectedmigrate(!data.drug_only ? 'Drug & Stock' : 'Drug Only');

                    if (typeof (data.status === "Validating" ? data?.validation_progress === "number" : data.progress === "number")) setProgressPercent((data.status === "Validating" ? data?.validation_progress : data.progress) || 0);

                    if (data.status === "Processing" || data.status === "In Progress" || data.status === "Validating") {
                        setProgressBar(true);
                        startPolling();
                    }
                }
            } catch (error) {
                setIsLoader(false);
                errorMessage(error?.response?.data?.message || error?.message);
            }
        };

        fetchCurrentStatus();

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, []);

    const getAPI = async () => {
        const clinicId = localGetItem('clinicId');
        setIsLoader(true);
        try {
            const response = await RestAPIService.getAll(
                `${api.progress}?clinic_id=${clinicId}&migration_type=${moduleName}`
            );

            if (response.data?.results?.status === 'success' || response.data?.status === 'success') {
                let data = response.data?.results?.data?.[0] || response.data?.data?.[0];
                if (!data) return;
                if (data?.status === 'Validation Failed') {
                    setMigrationId(null);
                    if (response.data?.results?.message === "Upload records fetched successfully.") {
                        successMessage(response.data?.results?.message);
                    } else {
                        errorMessage(response.data?.results?.message || response.data?.message || "Validation Failed. Please check the invalid records.");
                    }
                }
                if (moduleName === 'Pharmacy' && (data?.status === 'Validated' || data?.status === 'Failed' || data?.status === 'initiated')) {
                    setMigrationId(data.upload_id);
                }
                setSummary(response.data?.results?.data || response?.data?.data || []);
            }
        } catch (error) {
            errorMessage(error?.response?.data?.message || error?.message);
        } finally {
            setIsLoader(false);
        }
    };


    const handlePharmacyMigration = async (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        if (!migrationId) return;
        setIsLoader(true);
        try {
            const formData = new FormData();
            formData.append("action", "start_processing");
            formData.append("csv_file", selectedFile);
            formData.append("upload_id", migrationId);
            formData.append("drug_only", selectedmigrate === 'Drug Only' ? true : false);

            const response = await RestAPIService.create(formData, api.check);

            if (response.data.status === "success") {
                setProgressBar(true);
                setProgressPercent(0);
                successMessage("Migration started!");
                startPolling();
            }
        } catch (e) {
            errorMessage(e?.response?.data?.message || e?.message);
        } finally {
            e.target.value = null;
            setIsLoader(false);
        }
    };

    const handleFileChange = async (e) => {
        const clinicId = localGetItem('clinicId');
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        if ((selectedmigrate === null || selectedmigrate === undefined) && moduleName === 'Pharmacy') {
            errorMessage("Please select migration type");
            return;
        }
        setIsLoader(true);
        try {
            const formData = new FormData();
            formData.append("action", "init");
            formData.append("clinic_id", clinicId);
            formData.append("csv_file", selectedFile);
            if (moduleName === 'Pharmacy') {
                formData.append("drug_only", selectedmigrate === 'Drug Only' ? true : false);
            }
            const response = await RestAPIService.create(formData, api.check);
            if (response.data.status === "success" && moduleName === 'Pharmacy') {
                setValidating(true);
                setIsLoader(false);
                setProgressBar(true);
                setProgressPercent(0);
                successMessage("Validation started!");
                startPolling();

            } else if (response.data.status === "success" && moduleName !== 'Pharmacy') {
                const data = response.data.data;
                setMigrationId(data.upload_id);
                successMessage(response?.data?.message || "File uploaded and checked successfully!");
                await getAPI();
            }
        } catch (e) {
            errorMessage(e?.response?.data?.message || e?.message);
        } finally {
            e.target.value = null;
            setIsLoader(false);
        }
    };

    const startMigration = async () => {
        if (!migrationId) return;

        setIsLoader(true);
        try {
            const data = { upload_id: migrationId, action: "start_processing" };
            const response = await RestAPIService.create(data, api.start);

            if (response.data.status === "success") {
                setProgressBar(true);
                setProgressPercent(0);
                successMessage("Migration started!");
                startPolling();
            }
        } catch (e) {
            errorMessage(e?.response?.data?.message || e?.message);
        } finally {
            setIsLoader(false);
        }
    };

    const startPolling = () => {
        const clinicId = localGetItem("clinicId");

        if (pollingRef.current) clearInterval(pollingRef.current);

        pollingRef.current = setInterval(async () => {
            try {
                const response = await RestAPIService.getAll(`${api.progress}?clinic_id=${clinicId}&migration_type=${moduleName}`);

                if (response.data?.results?.status === "success" || response.data?.status === "success") {
                    const data = response.data?.results?.data?.[0] || response.data?.data?.[0];
                    if (!data) return;

                    setMigrationId(data.upload_id);
                    setSummary(response.data?.results?.data || response?.data?.data || []);

                    if (typeof (data.status === "Validating" ? data?.validation_progress === "number" : data.progress === "number")) setProgressPercent((data.status === "Validating" ? data?.validation_progress : data.progress) || 0);

                    if (data.status === "Validating") {
                        setValidating(true)
                    }

                    if (data.status === "Completed" || data.status === "Failed" || data.status === "Validation Failed" || data.status === "Validated") {
                        setMigrationId(null);
                        setProgressBar(false);
                        setValidating(false);
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                        if (data.status === "Completed") {
                            successMessage("Migration completed successfully!");
                        } else if (data.status === "Failed") {
                            errorMessage("Migration failed. Please check the invalid records.");
                        }
                        await getAPI();
                    }
                }
            } catch (error) {
                errorMessage(error?.response?.data?.message || error?.message);
                setProgressBar(false);
            }
        }, 5000);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    }

    const handleTempDownload = async () => {

        const clinicId = localGetItem('clinicId');
        setDownloading(true);

        try {
            const response = await RestAPIService.excelGet(`${api.download}?clinic_id=${clinicId}&type=template&migration_type=${moduleName}`);

            if (response.data) {
                const csvContent = response.data;
                const filename = response.headers["content-disposition"]
                    ?.split("filename=")[1]
                    ?.replace(/["']/g, "") || "registry.xlsx";

                const blob = new Blob([csvContent], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);

                successMessage("Template Downloaded successfully!");
            }
        } catch (error) {
            errorMessage(error.response?.data?.message || error.message);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownload = async (id) => {

        const clinicId = localGetItem('clinicId');
        setDownloading(true);

        try {
            const response = await RestAPIService.excelGet(
                `${api.download}?clinic_id=${clinicId}&upload_id=${id}&type=invalid&migration_type=${moduleName}`
            );

            if (response.data) {
                const csvContent = response.data;
                const filename = response.headers["content-disposition"]
                    ?.split("filename=")[1]
                    ?.replace(/["']/g, "") || "registry.xlsx";

                const blob = new Blob([csvContent], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);

                successMessage("File Downloaded successfully!");
            }
        } catch (error) {
            errorMessage(error.response?.data?.message || error.message);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ height: '79.5vh', width: '94vw', backgroundColor: Colors.Background, padding: '.5vw' }}>
            <Box sx={{ height: '72vh', width: '93vw', marginTop: '.5vw', backgroundColor: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} p="0.5vw">
                    <Button disabled={downloading} endIcon={<FileDownloadOutlined />} onClick={() => { handleTempDownload() }} variant='contained'>
                        {"Download Template"}
                    </Button>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.5vw" }}>
                        {moduleName === 'Pharmacy' ?
                            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5vw" }}>
                                <Autocomplete
                                    id="tags-outlined"
                                    options={migrateType}
                                    style={{ width: "15vw" }}
                                    disabled={(summary?.length > 0 && summary[0]?.status !== "Completed" && summary[0]?.status !== "Validation Failed" && moduleName === 'Pharmacy')}
                                    size='small'
                                    getOptionLabel={(option) => option}
                                    value={selectedmigrate}
                                    onChange={(event, newValue) => {
                                        setselectedmigrate(newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            disabled={(summary?.length > 0 && summary[0]?.status !== "Completed" && summary[0]?.status !== "Validation Failed" && moduleName === 'Pharmacy')}
                                            label={"Select Migration Type"}
                                            placeholder={'Select Type'}
                                        />
                                    )}
                                />
                            </Box>
                            : null}
                        {moduleName === 'Pharmacy' ?
                            <Button
                                component="label"
                                variant="contained"
                                color="success"
                                endIcon={<FileUploadOutlinedIcon />}
                                disabled={!migrationId || isLoader || (summary?.length > 0 && summary[0]?.status !== "Validated" && summary[0]?.status !== "Failed" && moduleName === 'Pharmacy')}
                            >
                                {'Migrate'}
                                <input
                                    type="file"
                                    accept=".xls,.xlsx,.csv"
                                    hidden
                                    onChange={handlePharmacyMigration}
                                />
                            </Button> : <Button
                                variant="contained"
                                color="success"
                                onClick={startMigration}
                                disabled={!migrationId || isLoader}
                            > Migrate </Button>}

                        <Button
                            component="label"
                            variant="contained"
                            endIcon={<FileUploadOutlinedIcon />}
                            disabled={isLoader || summary.status === "Processing" || summary?.length > 0 && (summary[0]?.status !== "Completed" && summary[0]?.status !== "Validation Failed" && summary[0]?.status !== "Failed" && moduleName === 'Pharmacy')}
                        >
                            {"Upload Excel/CSV"}
                            <input
                                type="file"
                                accept=".xls,.xlsx,.csv"
                                hidden
                                onChange={handleFileChange}
                            />
                        </Button>
                    </Box>
                </Box>

                {progressBar && (
                    <Fade in={true}>
                        <Box
                            sx={{
                                position: "absolute",
                                top: 120,
                                left: 90,
                                width: "93%",
                                height: "72%",
                                background: "rgba(0,0,0,0.5)",
                                backdropFilter: "blur(1px)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="h6" mb={2} fontWeight="600" color="primary">
                                {validating ? `Validation in Progress (${progressPercent}%)` : `Migration in Progress (${progressPercent}%)`}
                            </Typography>
                            <Box
                                sx={{
                                    width: "50%",
                                    borderRadius: "10px",
                                    overflow: "hidden",
                                    animation: `${glowAnim} 2s infinite ease-in-out`,
                                }}
                            >
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPercent}
                                    sx={{
                                        height: 12,
                                        borderRadius: 10,
                                        "& .MuiLinearProgress-bar": {
                                            background: "linear-gradient(90deg, #8bf2b6ff, #69F0AE)",
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Fade>
                )}

                <TableContainer sx={{ padding: '0.5vw' }}>
                    <Box sx={{ height: '62.5dvh', overflowY: 'auto' }}>
                        <Table size='small' stickyHeader={!progressBar}>
                            <TableHead sx={{ height: "5vh" }}>
                                <TableRow>
                                    <TableCell className='eMed_Pat_Bill_Tbl_Header' sx={{ width: "5%" }}>S.No</TableCell>
                                    <TableCell className='eMed_Pat_Bill_Tbl_Header' sx={{ width: "15%" }}>Date & Time</TableCell>
                                    <TableCell className='eMed_Pat_Bill_Tbl_Header' sx={{ width: "18%" }}>Total Records</TableCell>
                                    <TableCell className='eMed_Pat_Bill_Tbl_Header' sx={{ width: "18%" }}>Migrated Records</TableCell>
                                    <TableCell className='eMed_Pat_Bill_Tbl_Header' sx={{ width: "18%" }}>Invalid Records</TableCell>
                                    <TableCell className='eMed_Pat_Bill_Tbl_Header' sx={{ width: "13%" }} align='center'>Status</TableCell>
                                    <TableCell className='eMed_Pat_Bill_Tbl_Header' sx={{ width: "13%" }} align='center'>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summary?.length > 0 ? (summary.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                                    <>
                                        <TableRow key={index}>
                                            <TableCell sx={{ width: "5%" }}>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell sx={{ width: "15%" }}>{item?.created_at || '-'}</TableCell>
                                            <TableCell sx={{ width: "18%" }}>{item.total_records || 0}</TableCell>
                                            <TableCell sx={{ width: "18%" }}>{item.inserted_records || 0}</TableCell>
                                            <TableCell sx={{ width: "18%" }}>{item.invalid_records || 0}</TableCell>
                                            <TableCell sx={{ width: "13%" }} align='center'>
                                                <Typography sx={{ textTransform: 'capitalize' }} color={item?.status === 'Completed' ? "green" : item?.status === 'Not Initiated' ? 'GrayText' : "orange"}>{item?.status || '-'}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ width: "13%", }} align='center'>
                                                <Button onClick={() => { handleDownload(item?.upload_id) }} variant='contained' disabled={(item.invalid_records === 0 && moduleName !== 'Pharmacy') || (item.status !== 'Validated' && item.status !== 'Validation Failed' && item.status !== 'Failed' && moduleName === 'Pharmacy') || downloading || index !== 0 || progressBar}>
                                                    {"Download"}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                ))) : (<TableRow>
                                    <TableCell colSpan={7} align="center">{"No Records To Be Shown"}</TableCell>
                                </TableRow>)}
                            </TableBody >
                        </Table>
                    </Box>
                    <TablePagination
                        component='div'
                        id="eMed_tbl_pagination"
                        rowsPerPageOptions={[10, 20, 30]}
                        count={summary?.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </Box>

            <Loader loaderOpen={isLoader} />
            {errorMsg && (
                <ToastMsg severity="error" msg={errorMsgText} msgPop={msgClose} />
            )}
            {successMsg && (
                <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />
            )}
        </Box>
    );
}